import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import ModifierConfigurationSection from './components/ModifierConfigurationSection/ModifierConfigurationSection';
import ModifierOptionsSection from './components/ModifierOptionsSection/ModifierOptionsSection';
import './CreateModifierModal.scss';

interface ModifierOption {
  id: string;
  name: string;
  price_delivery: number;
  price_online: number;
  price_desk: number;
}

interface ModifierData {
  id: string;
  name: string;
  price_setter: 'add' | 'replace';
  mandatory: boolean;
  options: ModifierOption[];
}

interface CreateModifierModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (modifier: ModifierData) => void;
  currentItemName: string;
  editMode?: boolean;
  editData?: ModifierData;
}

const CreateModifierModal: React.FC<CreateModifierModalProps> = ({
  show,
  onHide,
  onSave,
  currentItemName,
  editMode = false,
  editData
}) => {
  const [modifierName, setModifierName] = useState('');
  const [priceSetter, setPriceSetter] = useState<'add' | 'replace'>('add');
  const [mandatory, setMandatory] = useState(false);
  const [options, setOptions] = useState<ModifierOption[]>([]);

  // Reset form when modal opens or populate with edit data
  useEffect(() => {
    if (show) {
      if (editMode && editData) {
        // Populate form with existing data
        setModifierName(editData.name);
        setPriceSetter(editData.price_setter);
        setMandatory(editData.mandatory);
        setOptions(editData.options);
      } else {
        // Reset form for new modifier
        setModifierName('');
        setPriceSetter('add');
        setMandatory(false);
        setOptions([]);
      }
    }
  }, [show, editMode, editData]);

  const handleSave = () => {
    // Validations
    if (!modifierName.trim()) {
      toast.error('El nombre del modificador es requerido');
      return;
    }

    if (options.length === 0) {
      toast.error('Debe agregar al menos una opción al modificador');
      return;
    }

    const modifier: ModifierData = {
      id: editMode && editData ? editData.id : `mod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: modifierName.trim(),
      price_setter: priceSetter,
      mandatory,
      options
    };

    onSave(modifier);
    onHide();
    toast.success(editMode ? 'Modificador actualizado exitosamente' : 'Modificador creado exitosamente');
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="create-modifier-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            {editMode ? 'Editar Modificador' : 'Crear Modificador'}
          </Modal.Title>
          <p className="form-modal-subtitle">
            Item base: <span className="current-item">{currentItemName}</span>
          </p>
        </div>
        <Button variant="none" className="modal-close-button" onClick={onHide}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <div className="modifier-form">
          <ModifierConfigurationSection
            modifierName={modifierName}
            setModifierName={setModifierName}
            priceSetter={priceSetter}
            setPriceSetter={setPriceSetter}
            mandatory={mandatory}
            setMandatory={setMandatory}
            currentItemName={currentItemName}
            optionsCount={options.length}
          />
          
          <ModifierOptionsSection
            options={options}
            setOptions={setOptions}
            modifierName={modifierName}
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" onClick={onHide}>
            Cancelar
          </Button>
          <Button 
            className="form-button-success" 
            onClick={handleSave}
            disabled={!modifierName.trim() || options.length === 0}
          >
            {editMode ? 'Actualizar Modificador' : 'Crear Modificador'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateModifierModal;
